import { useState } from 'react'
import { Pressable, View } from 'react-native'
import Animated, {
  interpolateColor,
  useAnimatedStyle,
} from 'react-native-reanimated'
import cn from '@ui/utils/cn'
import { colors } from '@ui/colors'
import Typography from '@ui/typography'
import Icon from '@ui/elements/Icon'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import { Collapsible } from '@ui/animated/Collapsible'
import { DropdownItemProps } from './types'

export default function Item({
  index,
  label,
  icon,
  iconComponent,
  destructive,
  disabled,
  items,
  className,
  ...props
}: DropdownItemProps & { index: number; className?: string }) {
  const { animation, onPressIn, onPressOut } = useAnimatedPress()
  const [expand, setExpand] = useState(false)

  const style = useAnimatedStyle(() => {
    const colorEnd = destructive ? colors.red25 : colors.black10
    return {
      backgroundColor: interpolateColor(
        animation.value,
        [0, 1],
        [colors.white10, colorEnd]
      ),
    }
  })

  const color = disabled
    ? colors.neutral[400]
    : destructive
    ? colors.red[600]
    : colors.text

  const onPress = () => {
    if (items && items.length > 0) {
      setExpand(!expand)
      return
    }

    props.onPress?.()
  }

  const itemsList = items?.filter((item) => !item.hidden)
  const hasItems = itemsList && itemsList.length > 0

  return (
    <>
      <Pressable
        onPress={onPress}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        disabled={disabled}
        style={[{ height: 48, minHeight: 48 }]}
        className={disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
      >
        <Animated.View
          className={cn(
            'relative inset-0',
            'flex flex-1 flex-row items-center justify-between border-black/10 px-6',
            index > 0 && 'border-t',
            className
          )}
          style={[style]}
        >
          <View className="flex-1 flex-row items-center gap-4">
            {icon && <Icon name={icon} color={color} />}
            {iconComponent}
            <Typography
              variant="SMB"
              className={cn('font-text-bold text-left')}
              color={color}
            >
              {label}
            </Typography>
          </View>
          {hasItems && (
            <Icon
              name={expand ? 'chevron-up' : 'chevron-down'}
              color={color}
              size={24}
              strokeWidth={2}
            />
          )}
        </Animated.View>
      </Pressable>
      {hasItems && (
        <Collapsible fullWidth open={expand}>
          {itemsList.map((item, i) => {
            const key = item.key ?? i.toString()
            return (
              <Item
                {...item}
                key={`${key}-child-${i}`}
                index={i + 1}
                onPress={() => {
                  item.onPress?.()
                  setExpand(false)
                }}
                className={cn('pl-16')}
              />
            )
          })}
        </Collapsible>
      )}
    </>
  )
}
